import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

/**
 * Iter XX.53 — Founder audit panel for Story Moments (SHADOW MODE).
 *
 * This page never surfaces anything to real users. It exists so the
 * founder can review 20-30 candidates before ANY live delivery.
 */
export default function FounderStoryMoments() {
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [discoverUserId, setDiscoverUserId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get("/founder/story-moments/candidates", { params: { status: statusFilter || undefined, limit: 100 } }),
        api.get("/founder/story-moments/stats"),
      ]);
      setCandidates(c.data.candidates || []);
      setStats(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load candidates");
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  async function runDiscovery() {
    if (!discoverUserId.trim()) { toast.error("user_id required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/founder/story-moments/discover",
                                  { user_id: discoverUserId.trim() });
      toast.success(`Found ${r.data.found} · +${r.data.inserted} new`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Discovery failed"); }
    finally { setBusy(false); }
  }

  async function redraft(sig) {
    try {
      await api.post(`/founder/story-moments/${sig}/draft`);
      await load();
      toast.success("Redrafted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Redraft failed"); }
  }

  async function decide(sig, status, extras = {}) {
    try {
      await api.post(`/founder/story-moments/${sig}/decision`, { status, ...extras });
      await load();
      toast.success(`Marked ${status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Decision failed"); }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-neutral-100" data-testid="fsm-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/admin" className="text-xs text-neutral-500 hover:text-neutral-300">← Command Center</Link>
        <h1 className="text-2xl font-display font-light mt-2 mb-1" data-testid="fsm-title">Story Moments · Shadow Mode</h1>
        <p className="text-sm text-neutral-500 mb-6">
          Nothing here is delivered to real users. Approve, edit or reject candidates before any pilot flag is turned on.
        </p>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="fsm-stats">
            <StatCard label="Total" value={stats.total} testid="fsm-stat-total" />
            <StatCard label="Reviewed" value={stats.reviewed} testid="fsm-stat-reviewed" />
            <StatCard label="Approval rate" value={stats.approval_rate != null ? `${stats.approval_rate}%` : "—"} testid="fsm-stat-approval" />
            <StatCard label="Grounding rate" value={stats.grounding_rate != null ? `${stats.grounding_rate}%` : "—"} testid="fsm-stat-grounding" />
          </div>
        )}

        <div className="bg-neutral-950 border border-neutral-900 rounded-xl p-4 mb-6" data-testid="fsm-discovery">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Run discovery</div>
          <div className="flex gap-2">
            <Input value={discoverUserId}
                   onChange={(e) => setDiscoverUserId(e.target.value)}
                   placeholder="user_id (e.g. demo_owner_demo_ca_academy)"
                   className="bg-neutral-900 border-neutral-800"
                   data-testid="fsm-discover-input"/>
            <Button onClick={runDiscovery} disabled={busy} data-testid="fsm-discover-btn"
                    className="bg-emerald-500 text-neutral-950 hover:bg-emerald-400">
              {busy ? "Running…" : "Discover"}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-neutral-500">Filter:</span>
          {["", "pending", "approved", "rejected", "unsafe", "not_meaningful", "edited"].map(v => (
            <button key={v || "all"}
                    onClick={() => setStatusFilter(v)}
                    className={`text-xs px-2 py-1 rounded-full border ${
                      statusFilter === v ? "border-emerald-600 text-emerald-300" : "border-neutral-800 text-neutral-400"
                    }`}
                    data-testid={`fsm-filter-${v || "all"}`}>
              {v || "all"}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-sm text-neutral-500">Loading…</div>
        ) : candidates.length === 0 ? (
          <div className="text-sm text-neutral-500" data-testid="fsm-empty">
            No candidates yet. Run discovery on a user with real conversation + match history.
          </div>
        ) : (
          <div className="space-y-4">
            {candidates.map(c => (
              <CandidateCard key={c.signature} c={c}
                              onRedraft={() => redraft(c.signature)}
                              onDecide={(status, extras) => decide(c.signature, status, extras)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-950 p-3" data-testid={testid}>
      <div className="text-2xl font-semibold text-neutral-100">{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">{label}</div>
    </div>
  );
}

function CandidateCard({ c, onRedraft, onDecide }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(c.draft || "");
  const status = c.decision?.status || "pending";
  const lintOk = c.draft_lint_ok !== false;
  return (
    <div className="rounded-xl border border-neutral-900 bg-neutral-950 p-5" data-testid={`fsm-candidate-${c.signature}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-400/80">{c.kind}</div>
          <div className="text-sm text-neutral-500">user: {c.user_id} · conf {Math.round((c.confidence || 0) * 100)}%</div>
        </div>
        <StatusPill status={status} />
      </div>

      <div className="text-xs text-neutral-500 mb-3" data-testid={`fsm-reason-${c.signature}`}>
        <span className="text-neutral-400 uppercase tracking-widest text-[10px]">Trigger:</span> {c.reason}
      </div>

      <div className="rounded-lg border border-neutral-900 bg-black/40 p-4 mb-3 whitespace-pre-wrap text-neutral-100"
           data-testid={`fsm-draft-${c.signature}`}>
        {editing ? (
          <textarea value={editText} onChange={(e) => setEditText(e.target.value)}
                    className="w-full bg-transparent border border-neutral-800 rounded p-2 text-sm"
                    rows={5}
                    data-testid={`fsm-edit-textarea-${c.signature}`} />
        ) : (
          c.draft || <span className="text-neutral-600 italic">No draft yet</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
        <span className={`px-2 py-0.5 rounded-full border ${lintOk ? "border-emerald-800 text-emerald-300" : "border-red-800 text-red-300"}`}>
          lint {lintOk ? "ok" : "failed"}
        </span>
        {c.draft_lint_reason && <span className="text-red-300">{c.draft_lint_reason}</span>}
        <span className="text-neutral-500">source: {c.draft_source || "—"}</span>
        {c.commitment_snapshot && (
          <span className="text-neutral-500">tier: {c.commitment_snapshot.tier || "unknown"}</span>
        )}
        <span className={`px-2 py-0.5 rounded-full border ${c.gate_would_deliver ? "border-emerald-800 text-emerald-300" : "border-neutral-800 text-neutral-500"}`}>
          {c.gate_would_deliver ? "gate: would deliver" : "gate: would not deliver"}
        </span>
      </div>

      <details className="mb-3">
        <summary className="text-xs text-neutral-500 cursor-pointer">Evidence ({(c.evidence || []).length})</summary>
        <div className="mt-2 space-y-2">
          {(c.evidence || []).map((e, i) => (
            <div key={i} className="text-xs text-neutral-400 border-l-2 border-neutral-800 pl-3">
              <div className="text-neutral-500">{e.kind} · {e.date}</div>
              {e.excerpt && <div className="text-neutral-300 italic">&ldquo;{e.excerpt}&rdquo;</div>}
              {e.numeric && <div className="font-mono text-[11px] text-neutral-500">{JSON.stringify(e.numeric)}</div>}
            </div>
          ))}
          {c.draft_rejected_llm && (
            <div className="text-xs text-red-400 mt-3">
              <div className="text-neutral-500">LLM draft rejected — reason: {c.draft_rejected_reason}</div>
              <div className="mt-1 italic text-red-300 whitespace-pre-wrap">{c.draft_rejected_llm}</div>
            </div>
          )}
        </div>
      </details>

      <div className="flex flex-wrap gap-2">
        {!editing ? (
          <>
            <Button size="sm" onClick={() => onDecide("approved")}
                    className="bg-emerald-600 hover:bg-emerald-500 text-neutral-950"
                    data-testid={`fsm-approve-${c.signature}`}>Approve</Button>
            <Button size="sm" onClick={() => onDecide("rejected")}
                    variant="outline" className="border-neutral-800"
                    data-testid={`fsm-reject-${c.signature}`}>Reject</Button>
            <Button size="sm" onClick={() => onDecide("unsafe")}
                    variant="outline" className="border-red-900 text-red-300 hover:bg-red-950"
                    data-testid={`fsm-unsafe-${c.signature}`}>Unsafe</Button>
            <Button size="sm" onClick={() => onDecide("not_meaningful")}
                    variant="outline" className="border-neutral-800 text-neutral-400"
                    data-testid={`fsm-not-meaningful-${c.signature}`}>Not meaningful</Button>
            <Button size="sm" onClick={() => setEditing(true)}
                    variant="outline" className="border-neutral-800"
                    data-testid={`fsm-edit-${c.signature}`}>Edit</Button>
            <Button size="sm" onClick={onRedraft}
                    variant="outline" className="border-neutral-800"
                    data-testid={`fsm-redraft-${c.signature}`}>Redraft</Button>
          </>
        ) : (
          <>
            <Button size="sm" onClick={() => { onDecide("edited", { edited_message: editText }); setEditing(false); }}
                    className="bg-emerald-600 hover:bg-emerald-500 text-neutral-950"
                    data-testid={`fsm-edit-save-${c.signature}`}>Save edit</Button>
            <Button size="sm" onClick={() => setEditing(false)} variant="outline"
                    className="border-neutral-800"
                    data-testid={`fsm-edit-cancel-${c.signature}`}>Cancel</Button>
          </>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const cls = status === "approved" ? "border-emerald-800 text-emerald-300"
            : status === "rejected" ? "border-red-900 text-red-300"
            : status === "unsafe"   ? "border-red-900 text-red-300 bg-red-950/20"
            : status === "not_meaningful" ? "border-neutral-800 text-neutral-500"
            : status === "edited"   ? "border-amber-900 text-amber-300"
            : "border-neutral-800 text-neutral-400";
  return <span className={`text-xs px-2 py-0.5 rounded-full border ${cls}`}>{status}</span>;
}
